<template>
  <div    v-if="modalExists"
          class="fixed z-50 inset-0 overflow-y-auto"
          @click="close">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!--
        Background overlay, show/hide based on modal state.

        Entering: "ease-out duration-300"
          From: "opacity-0"
          To: "opacity-100"
        Leaving: "ease-in duration-200"
          From: "opacity-100"
          To: "opacity-0"
      -->
      <transition
          enter-active-class="ease-in-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in-out duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0">
        <div    v-show="isOpen"
                class="fixed inset-0 transition-opacity">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
      </transition>

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
      <!--
        Modal panel, show/hide based on modal state.

        Entering: "ease-out duration-300"
          From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          To: "opacity-100 translate-y-0 sm:scale-100"
        Leaving: "ease-in duration-200"
          From: "opacity-100 translate-y-0 sm:scale-100"
          To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      -->
      <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:scale-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100 translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
        <div    v-show="isOpen"
                class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden
                            shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
                @click.stop="">
          <div>
            <slot name="icon">
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <span class="ml-1 lnr icon-watch h-6 w-6 text-green-600"></span>
              </div>
            </slot>
            <div class="mt-2 text-center sm:mt-5">
              <div class="mt-2">
                <p class="text-md leading-5 text-gray-500 mb-8">
                  Set the time using the controls below
                </p>
                <span class="flex w-full justify-center rounded-md mt-4">
                  <button type="button"
                          class="inline-flex justify-center w-1/3 rounded-md border border-transparent px-4 py-2
                                        bg-red-400 text-base leading-6 font-medium text-white shadow-sm
                                        hover:bg-red-500 focus:outline-none focus:border-red-700
                                        focus:ring-red-200 sm:text-sm sm:leading-5"
                          @click="close(true)">
                      Reset Time
                  </button>
                </span>
                <div class="grid grid-cols-3 gap-2">
                  <div class="col-span-1" @click="addMinutes(60)">
                    <span class="lnr icon-chevron-up"></span>
                  </div>
                  <div class="col-span-1"></div>
                  <div class="col-span-1" @click="addMinutes(minuteStep)">
                    <span class="lnr icon-chevron-up"></span>
                  </div>
                  <div class="col-span-1">
                    <input type="text" class="w-1/2 border border-gray-300 text-gray-500 text-center text-xl rounded-lg p-2"
                           disabled
                           :value="paddedHours">
                  </div>
                  <div class="col-span-1"> :</div>
                  <div class="col-span-1">
                    <input type="text" class="w-1/2 border border-gray-300 text-gray-500 text-center text-xl rounded-lg p-2"
                           disabled
                           :value="paddedMinutes">
                  </div>
                  <div class="col-span-1" @click="addMinutes(-60)">
                    <span class="lnr icon-chevron-down"></span>
                  </div>
                  <div class="col-span-1"></div>
                  <div class="col-span-1" @click="addMinutes(-minuteStep)">
                    <span class="lnr icon-chevron-down"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-6">
            <span class="flex w-full rounded-md shadow-sm">
              <button type="button"
                      class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2
                                        bg-blue-600 text-base leading-6 font-medium text-white shadow-sm
                                        hover:bg-blue-500 focus:outline-none focus:border-blue-700
                                        focus:ring-blue-200 sm:text-sm sm:leading-5"
                      @click="close(false)">
                <slot   name="button-content">
                  Okay
                </slot>
              </button>
            </span>

          </div>
        </div>
      </transition>

    </div>
  </div>

</template>

<script>


const CLOSE_DELAY = 200

export default {
  name: "hourMinutePicker",
  mixins: [],
  components: {},
  props: {
    isOpen: {
      type: Boolean,
      default: true,
    },
    seconds: {
      type: Number,
      default: 0
    },
    /**
     * Optional "HH:MM" string. When provided (non-null), takes precedence over `seconds`
     * for initializing the picker. Parents that prefer to think in clock-time can pass this
     * instead of computing seconds themselves.
     */
    time: {
      type: String,
      default: null,
    },
    minuteStep: {
      type: Number,
      default: 5,
    },
    minSeconds: {
      type: Number,
      default: 300,
    },
    maxSeconds: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      modalExists: this.isOpen,
      inputValue: this.initialInputValue(),
    }
  },
  mounted() {

  },
  methods: {
    initialInputValue() {
      // The initial value is based off of this.time if it exists and is valid,  otherwise we fall back to this.seconds  
      if (this.time !== null && typeof this.time === 'string') {
        return this.parseTimeToSeconds(this.time);
      }
      return isNaN(this.seconds) ? 35 * 60 : this.seconds;
    },
    parseTimeToSeconds(str) {
      const parts = String(str).split(':');
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1] ?? '0', 10) || 0;
      return h * 3600 + m * 60;
    },
    close(shouldNull=false) {
      this.$emit('update:isOpen', false)
      this.$emit('datePicked', shouldNull ? null : this.buildPayload());
    },
    addMinutes(val){
      let newResult = this.inputValue + (val * 60);
      if(newResult < this.minSeconds){
        newResult = this.minSeconds;
      }
      if(this.maxSeconds !== null && newResult > this.maxSeconds){
        newResult = this.maxSeconds;
      }
      this.inputValue = newResult
    },
    buildPayload() {
      return {
        seconds: this.inputValue,
        hours: this.hoursFromSeconds,
        minutes: this.minutesFromSeconds,
        time: this.formattedTime,
        hhmm: this.formattedHhmm,
      };
    },
  },
  watch: {
    isOpen() {
      setTimeout(() => {
        this.modalExists = this.isOpen
      }, CLOSE_DELAY)
    },
    seconds(){
      // Only honor the seconds prop when no `time` prop was provided.
      if (this.time === null) {
        this.inputValue = isNaN(this.seconds) ? 35 * 60 : this.seconds;
      }
    },
    time(val) {
      if (val !== null && typeof val === 'string') {
        this.inputValue = this.parseTimeToSeconds(val);
      }
    },
    inputValue(val) {
      // Live updates so parents using v-model:hours / :minutes / :time / :seconds stay in sync.
      this.$emit('update:seconds', val);
      this.$emit('update:hours', this.hoursFromSeconds);
      this.$emit('update:minutes', this.minutesFromSeconds);
      this.$emit('update:time', this.formattedTime);
    },
  },
  computed: {
    hoursFromSeconds(){
      return Math.floor(this.inputValue / 3600);
    },
    minutesFromSeconds(){
      return Math.floor((this.inputValue % 3600) / 60);
    },
    paddedHours(){
      return String(this.hoursFromSeconds).padStart(2, '0');
    },
    paddedMinutes(){
      return String(this.minutesFromSeconds).padStart(2, '0');
    },
    formattedTime(){
      const h = String(this.hoursFromSeconds).padStart(2, '0');
      const m = String(this.minutesFromSeconds).padStart(2, '0');
      return `${h}:${m}`;
    },
    formattedHhmm(){
      const h = String(this.hoursFromSeconds).padStart(2, '0');
      const m = String(this.minutesFromSeconds).padStart(2, '0');
      return `${h}${m}`;
    },
  },
}
</script>

<style scoped>

</style>